// Listens to V1's AgentForge WebSocket for one session and writes what it
// hears into the store, so the chat surface can render the assistant's
// reply as it streams in.
//
// V1 owns its own socket (`useAgentForgeWS`) — it doesn't share legacy's
// `useMissionControlWS`. Mount this hook exactly once (in the `AgentForge`
// umbrella) so each frame is processed a single time.
//
// Lifecycle mirrors legacy `/agentforge` ChatPanel:
//   • `stream_chunk` → append to the `streamingDraft` accumulator (live UX).
//   • `tool_start` / `tool_result` / `action_progress` → sub-step rows.
//   • `stream_done` / `agent_message` → clear draft, flip tab to `done`,
//     and invalidate the messages query so TanStack refetches the
//     persisted record from the server.
//   • `stream_error` → same teardown plus a console log.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { messagesKey } from '../constants';
import {
  structuredDataToBlock,
  toCandidateProfilesBlock,
  wantsFullProfiles,
  leaksToolNames,
} from '../helpers';
import { useAgentForgeStore } from '../stores';
import { refreshAndGetKulaToken } from '../api/kulaToken';
import { useAgentForgeWS, type AgentForgeWSMessage } from './useAgentForgeWS';

// Read a field as a string, falling back to `fallback` (default '') when
// missing or wrong type. Keeps the event handlers below short.
const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const TOOL_NAME_FALLBACK = 'Running tool';
const STREAM_ERROR_FALLBACK =
  'Something went wrong while generating a response.';

// Events that write into the per-session streaming draft. They carry a
// `turn_id` (the RunAgent workflow id) so we can tell two overlapping turns
// apart — the store keeps ONE draft per session, so only one turn may own
// it at a time. `block_chips_update` is deliberately absent: it patches an
// already-rendered block by tool_call_id and is safe from any turn.
const TURN_SCOPED_EVENTS = new Set([
  'stream_chunk',
  'stream_reset',
  'tool_start',
  'tool_result',
  'action_progress',
  'stream_done',
  'stream_error',
]);

// A turn that hasn't produced an event in this long is considered dead
// (worker crashed without a stream_done) — a newer turn may take over the
// draft. Long enough to sit out slow tools (resume parsing, semantic
// search), short enough that a wedged turn can't block the session.
const STALE_TURN_MS = 30_000;

// A streaming draft that goes silent this long is a zombie — created by a
// straggler event after its turn's stream_done (chip patches racing the turn
// end, replayed events after a reconnect, a turn killed by a worker
// restart). Zombies are poison: they read as "still streaming", which blocks
// the send gate AND makes the hydrate hook skip — so nothing ever clears
// them. The watchdog below commits + clears any draft this stale. Sized
// above the slowest single tool call (kula client cap 40s) so a live turn
// mid-tool is never cut.
const STALE_DRAFT_MS = 90_000;

// Action-progress events use a `status` string; map it to our 3-state union.
const mapActionStatus = (raw: string): 'active' | 'done' | 'error' => {
  if (raw === 'done' || raw === 'completed') return 'done';
  if (raw === 'failed' || raw === 'error') return 'error';
  return 'active';
};

export const useAgentForgeStream = (
  sessionId: string | null | undefined
): { connected: boolean } => {
  const { connected, reconnecting, subscribe, send, reconnectGeneration } =
    useAgentForgeWS();
  const queryClient = useQueryClient();

  // Backfill on every reconnect. Even with the server-side Redis Streams
  // replay there are gaps the stream can't cover (events older than the
  // replay cap, or the very first event after a long-disconnected
  // session). Invalidating the messages query is cheap and idempotent —
  // TanStack refetches the canonical transcript from the server and
  // reconciles. Skip on generation 0 (the very first connect — nothing
  // to backfill yet, the page-mount fetch already ran).
  useEffect(() => {
    if (!sessionId || reconnectGeneration === 0) return;
    queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
  }, [reconnectGeneration, sessionId, queryClient]);

  const appendStreamDelta = useAgentForgeStore((s) => s.appendStreamDelta);
  const resetStreamDraft = useAgentForgeStore((s) => s.resetStreamDraft);
  const pushSubStep = useAgentForgeStore((s) => s.pushSubStep);
  const patchSubStep = useAgentForgeStore((s) => s.patchSubStep);
  const pushOutputBlock = useAgentForgeStore((s) => s.pushOutputBlock);
  const patchBlockChips = useAgentForgeStore((s) => s.patchBlockChips);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);
  const commitStreamingDraft = useAgentForgeStore(
    (s) => s.commitStreamingDraft
  );
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);

  useEffect(() => {
    if (!sessionId) return undefined;

    // Turn-ownership bookkeeping (which turn owns the draft, which turn
    // last committed, when the owner last spoke) lives in the STORE
    // (turnScopeBySession) — an effect-closure variable here would reset on
    // every remount (HMR, session switches) and forget the owner mid-turn.
    const turnScope = () =>
      useAgentForgeStore.getState().turnScopeBySession[sessionId] ?? {
        activeTurn: null,
        lastDoneTurn: null,
        lastAcceptedAt: 0,
      };
    const setTurnScope = useAgentForgeStore.getState().setTurnScope;

    // At run-end commit the WS draft into a permanent assistant message AND
    // invalidate the server transcript query so `useAgentForgeMessages`
    // refetches with the now-complete record (including the final assistant
    // text saved by the worker after stream_done). The hydrate hook skips
    // mid-stream, so the refetch only takes effect now that streaming is
    // flagged done.
    const commitFromServer = () => {
      setTurnScope(sessionId, { activeTurn: null, lastAcceptedAt: 0 });
      commitStreamingDraft();
      setTabStatus(sessionId, 'done');
      queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
    };

    type Handler = (_payload: Record<string, unknown>) => void;
    const handlers: Record<string, Handler> = {
      // Append a token to the live assistant draft.
      stream_chunk: (p) => {
        const delta = asString(p.delta);
        if (delta) appendStreamDelta(sessionId, delta);
      },

      // A streaming attempt is (re)starting — drop any partial draft text so a
      // retried/re-streamed LLM call doesn't duplicate the shown prefix.
      stream_reset: () => resetStreamDraft(sessionId),

      // A tool just started running. Add it as an "active" sub-step row.
      tool_start: (p) => {
        pushSubStep(sessionId, {
          id: asString(p.tool_call_id, `tool-${Date.now()}`),
          name: asString(p.tool_name, TOOL_NAME_FALLBACK),
          description: asString(p.summary) || undefined,
          status: 'active',
        });
      },

      // A tool finished. Flip its row to "done" or "error", and — if the
      // backend shipped a structured_data payload — turn it into a rich
      // BlockOutput (candidate list, action result, etc.) and push it onto
      // the streaming draft so the renderer picks it up alongside the text.
      tool_result: (p) => {
        const id = asString(p.tool_call_id);
        if (!id) return;
        const hasError = asString(p.error).length > 0;
        patchSubStep(sessionId, id, {
          status: hasError ? 'error' : 'done',
          description: asString(p.summary) || undefined,
          durationMs: asNumber(p.duration_ms),
        });
        if (!hasError) {
          let block = structuredDataToBlock(p.structured_data, id);
          // INTERIM: promote to the full-profile carousel when asked.
          if (block?.type === 'candidate') {
            const msgs =
              useAgentForgeStore.getState().messagesBySession[sessionId] ?? [];
            const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
            if (wantsFullProfiles(lastUser?.content)) {
              block = toCandidateProfilesBlock(block);
            }
          }
          if (block) pushOutputBlock(sessionId, block);
        }
      },

      // Backend produced LLM-tailored follow-up chips ~500ms after a card
      // first rendered. Swap the static chips for the smart ones in place.
      block_chips_update: (p) => {
        const id = asString(p.tool_call_id);
        if (!id) return;
        const rawChips = Array.isArray(p.chips) ? p.chips : [];
        const chips = rawChips
          .map((c, i) => {
            const obj = c as {
              label?: unknown;
              prompt?: unknown;
              kind?: unknown;
            };
            const label = asString(obj.label);
            const prompt = asString(obj.prompt);
            if (!label || !prompt) return null;
            // Mirror of the backend filter — never render a chip that
            // leaks internal tool names into user-visible text.
            if (leaksToolNames(label) || leaksToolNames(prompt)) return null;
            const kind = obj.kind === 'primary' ? 'primary' : 'secondary';
            return {
              id: `chip-${id}-${i}`,
              label,
              promptTemplate: prompt,
              kind: kind as 'primary' | 'secondary',
            };
          })
          .filter((c): c is NonNullable<typeof c> => c !== null);
        if (chips.length === 0) return;
        patchBlockChips(sessionId, id, chips);
      },

      // Non-tool timeline step (routing, context-load, LLM composing, etc).
      // Keyed by `step` name with an `action:` prefix so action rows and
      // tool rows can coexist in the same `subSteps` list. The backend
      // ships an optional `label` (human-readable) and `duration_ms` —
      // when present the row reads "Loading conversation context · 320 ms"
      // rather than "context".
      action_progress: (p) => {
        const step = asString(p.step);
        if (!step) return;
        const status = mapActionStatus(asString(p.status, 'active'));
        const label = asString(p.label);
        const durationMs = asNumber(p.duration_ms);
        // pushSubStep dedups by id and merges status/description/duration,
        // so emitting `active` then `done` for the same step id updates
        // the same row instead of creating a second one.
        pushSubStep(sessionId, {
          id: `action:${step}`,
          name: label || step,
          description: label && label !== step ? undefined : undefined,
          status,
          ...(typeof durationMs === 'number' ? { durationMs } : {}),
        });
      },

      // A destructive/supervised tool call parked its workflow on a human
      // decision. Render the approve/reject card in the draft — the turn
      // stays open (no stream_done yet) until the user decides or the
      // server-side approval window auto-rejects. Deliberately NOT
      // turn-gated: dropping it would leave an invisible wait.
      approval_request: (p) => {
        const recordId = asString(p.record_id);
        if (!recordId) return; // no record → nothing to decide against
        pushOutputBlock(sessionId, {
          type: 'approval',
          data: {
            recordId,
            toolName: asString(p.tool_name, 'this action'),
            reason: asString(p.reason) || undefined,
            expiresAt: asNumber(p.expires_at) || undefined,
          },
        });
      },

      // Run is complete — the server has the final message. Replace our
      // accumulator with it (handles tool-loop turns where intermediate
      // chunks would otherwise show as garbled text).
      stream_done: commitFromServer,
      agent_message: commitFromServer,

      // Surface the failure IN the thread — a silent console.error left the
      // user staring at a spinner that just vanished. The raw error stays in
      // dev tools; the bubble gets a friendly retry instruction. The partial
      // draft (any tool cards that did complete) is committed with the error
      // block appended, then the canonical record is refetched.
      stream_error: (p) => {
        console.error(
          'agentforge stream error:',
          asString(p.error) || STREAM_ERROR_FALLBACK
        );
        pushOutputBlock(sessionId, {
          type: 'error',
          data: {
            message:
              'Something went wrong while generating this reply. Please send your message again.',
            retryable: false,
          },
        });
        setTurnScope(sessionId, { activeTurn: null, lastAcceptedAt: 0 });
        commitStreamingDraft();
        setTabStatus(sessionId, 'error');
        queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
      },
    };

    const unsubscribe = subscribe((msg: AgentForgeWSMessage) => {
      // Kula token refresh is user-scoped, not session-scoped: the backend saw a
      // 401 and is asking for a fresh token. Handle it before the session filter
      // and regardless of which session triggered it.
      if (msg.type === 'kula.token_expired') {
        // Backend's stored token is stale and it needs one to do work. Refresh
        // if needed and push the fresh token straight back over the WS — all
        // token communication stays on the socket.
        void refreshAndGetKulaToken().then((token) => {
          if (token) send({ type: 'kula.token', token });
        });
        return;
      }
      const payload = msg.payload ?? {};
      // Ignore frames meant for a different session.
      if (payload.session_id !== sessionId) return;
      // One turn owns the streaming draft at a time. Draft-writing events
      // from a different turn are dropped — their content still lands via
      // the transcript refetch when the owning turn commits — unless the
      // owning turn has gone silent long enough to be presumed dead, in
      // which case the newcomer salvages what's on screen and takes over.
      // Events without a turn_id (older backend, pre-turn server emits
      // like intake-classify) pass through unscoped.
      const turnId = asString(payload.turn_id);
      if (turnId && TURN_SCOPED_EVENTS.has(msg.type)) {
        const scope = turnScope();
        // Straggler from a turn that already committed — dropping it is the
        // only safe move (processing would open a zombie draft with no
        // stream_done left to close it). Its content is already in the
        // refetched transcript.
        if (turnId === scope.lastDoneTurn) return;
        if (scope.activeTurn && scope.activeTurn !== turnId) {
          if (Date.now() - scope.lastAcceptedAt < STALE_TURN_MS) return;
          commitStreamingDraft();
        }
        setTurnScope(sessionId, {
          activeTurn: turnId,
          lastAcceptedAt: Date.now(),
          ...(msg.type === 'stream_done' || msg.type === 'stream_error'
            ? { lastDoneTurn: turnId }
            : {}),
        });
      }
      const handler = handlers[msg.type];
      if (!handler) {
        // An unknown event type means the backend shipped something this
        // build doesn't understand — log it so a stale frontend is visible
        // in dev tools / log drains instead of failing silently.
        console.warn('agentforge: unhandled WS event type', msg.type);
        return;
      }
      handler(payload);
    });

    return unsubscribe;
  }, [
    sessionId,
    subscribe,
    send,
    appendStreamDelta,
    resetStreamDraft,
    pushSubStep,
    patchSubStep,
    pushOutputBlock,
    patchBlockChips,
    clearStreamingDraft,
    commitStreamingDraft,
    setTabStatus,
    queryClient,
  ]);

  // Zombie-draft watchdog. A draft that stops receiving events without a
  // stream_done (straggler event after commit, worker killed mid-turn,
  // replay artifacts) would otherwise wedge the whole session: the send
  // gate reads it as "streaming" and the hydrate hook skips — so nothing
  // ever clears it. Commit whatever it shows, flip the tab off, and refetch
  // the canonical transcript. Re-armed on every draft update; a live stream
  // keeps pushing the deadline forward.
  const streamingDraft = useAgentForgeStore((s) => s.streamingDraft);
  useEffect(() => {
    if (!streamingDraft) return undefined;
    const age = Date.now() - (streamingDraft.updatedAt ?? 0);
    const wait = Math.max(STALE_DRAFT_MS - age, 1_000);
    const timer = setTimeout(() => {
      const state = useAgentForgeStore.getState();
      // Only act if the exact same draft object is still there — any event
      // since produced a new object and re-armed a fresh watchdog.
      if (state.streamingDraft !== streamingDraft) return;
      const sid = streamingDraft.sessionId;
      state.commitStreamingDraft();
      state.setTabStatus(sid, 'done');
      queryClient.invalidateQueries({ queryKey: messagesKey(sid) });
    }, wait);
    return () => clearTimeout(timer);
  }, [streamingDraft, queryClient]);

  // Switching sessions mid-stream shouldn't leak the previous session's
  // accumulator into the new view — wipe the draft on session change.
  useEffect(() => clearStreamingDraft, [sessionId, clearStreamingDraft]);

  return { connected, reconnecting };
};
